import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, mergeMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../shared/services';
import { Contact } from '../../shared/models';
import * as CrmActions from './crm.actions';


@Injectable()
export class CrmEffects {

  refreshTimelineAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateMatterSuccess),
      map(({ matterId }) => CrmActions.loadMatterTimeline({ matterId: matterId ?? '', order: 'desc', limit: 20 }))
    )
  );

  createContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createContact),
      switchMap(({ contact }) =>
        this.apiService.createContact(contact).pipe(
          map((createdContact) => CrmActions.createContactSuccess({ contact: createdContact })),
          catchError((error) =>
            of(CrmActions.createContactFailure({
              error: error?.error?.message || 'Failed to create contact',
            }))
          )
        )
      )
    )
  );

  createMatter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createMatter),
      switchMap(({ matter }) =>
        this.apiService.createMatter(matter).pipe(
          map((createdMatter) => CrmActions.createMatterSuccess({ matter: createdMatter })),
          catchError((error) =>
            of(CrmActions.createMatterFailure({
              error: error?.error?.message || 'Failed to create matter',
            }))
          )
        )
      )
    )
  );

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadContacts),
      switchMap(({ skip, limit, search, contactType }) => {
        return this.apiService.getContacts({ skip, limit, search, type: contactType }).pipe(
          map((response: any) => {
            const data = response?.data ?? response;
            const total = response?.total ?? (Array.isArray(data) ? data.length : 0);
            return CrmActions.loadContactsSuccess({ contacts: data, total });
          }),
          catchError((error) => {
            return of(CrmActions.loadContactsFailure({
              error: error?.error?.message || 'Failed to load contacts',
            }));
          })
        );
      })
    )
  );

  loadContactDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadContactDetail),
      switchMap(({ contactId }) =>
        this.apiService.getContact(contactId).pipe(
          map((contact) => CrmActions.loadContactDetailSuccess({ contact: contact as Contact })),
          catchError((error) =>
            of(CrmActions.loadContactDetailFailure({
              error: error?.error?.message || 'Failed to load contact',
            }))
          )
        )
      )
    )
  );

  loadMatters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadMatters),
      switchMap(({ skip, limit, status, contactId }) =>
        this.apiService.getMatters({ skip, limit, status, contactId }).pipe(
          map(({ data, total }) =>
            CrmActions.loadMattersSuccess({ matters: data, total })
          ),
          catchError((error) =>
            of(CrmActions.loadMattersFailure({
              error: error?.error?.message || 'Failed to load matters',
            }))
          )
        )
      )
    )
  );

  loadMatterDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadMatterDetail),
      switchMap(({ matterId }) =>
        this.apiService.getMatter(matterId).pipe(
          map((matter) => CrmActions.loadMatterDetailSuccess({ matter })),
          catchError((error) =>
            of(CrmActions.loadMatterDetailFailure({
              error: error?.error?.message || 'Failed to load matter',
            }))
          )
        )
      )
    )
  );

  loadMatterTimeline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadMatterTimeline),
      switchMap(({ matterId, cursor, order, limit }) =>
        this.apiService.getMatterTimeline(matterId, { cursor, order, limit }).pipe(
          map(({ data, nextCursor }) =>
            CrmActions.loadMatterTimelineSuccess({
              timeline: data ?? [],
              nextCursor,
            })
          ),
          catchError((error) =>
            of(CrmActions.loadMatterTimelineFailure({
              error: error?.error?.message || 'Failed to load timeline',
            }))
          )
        )
      )
    )
  );

  loadBillingEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadBillingEvents),
      switchMap(({ matterId }) =>
        this.apiService.getBillingEvents(matterId).pipe(
          map(({ data, totalAmount }) =>
            CrmActions.loadBillingEventsSuccess({ events: data, totalAmount })
          ),
          catchError((error) =>
            of(CrmActions.loadBillingEventsFailure({
              error: error?.error?.message || 'Failed to load billing events',
            }))
          )
        )
      )
    )
  );

  addBillingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.addBillingEvent),
      switchMap(({ matterId, request, optimisticEvent }) =>
        this.apiService.createBillingEvent(matterId, request).pipe(
          map((event) => CrmActions.addBillingEventSuccess({ matterId, event })),
          catchError((error) =>
            of(CrmActions.addBillingEventFailure({
              error: error?.error?.message || 'Failed to add billing event',
            }))
          )
        )
      )
    )
  );

  refreshBillingAfterAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.addBillingEventSuccess),
      map(({ matterId }) => CrmActions.loadBillingEvents({ matterId }))
    )
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateContact),
      switchMap(({ contactId, contact }) =>
        this.apiService.updateContact(contactId, contact).pipe(
          map((resp) => {
            const updatedContact = (resp && (resp as any).value) ? (resp as any).value : resp;
            return CrmActions.updateContactSuccess({ contact: updatedContact as Contact, contactId });
          }),
          catchError((error) =>
            of(CrmActions.updateContactFailure({
              error: error?.error?.message || 'Failed to update contact',
              contactId,
            }))
          )
        )
      )
    )
  );

  updateMatter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateMatter),
      switchMap(({ matterId, matter }) =>
        this.apiService.updateMatter(matterId, matter).pipe(
          map((updatedMatter) =>
            CrmActions.updateMatterSuccess({ matter: updatedMatter, matterId })
          ),
          catchError((error) =>
            of(CrmActions.updateMatterFailure({
              error: error?.error?.message || 'Failed to update matter',
              matterId,
            }))
          )
        )
      )
    )
  );

  refreshContactAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateContactSuccess),
      map(({ contact, contactId }) =>
        CrmActions.loadContactDetail({ contactId: contactId ?? String((contact as any)?._id) })
      )
    )
  );

  refreshMatterAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateMatterSuccess),
      map(({ matter, matterId }) =>
        CrmActions.loadMatterDetail({ matterId: matterId ?? String((matter as any)?._id) })
      )
    )
  );

  createIntake$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createIntake),
      switchMap(({ contact, matter }) =>
        this.apiService.createContact({ ...contact, type: 'client' }).pipe(
          switchMap((createdContact) =>
            this.apiService
              .createMatter({
                ...matter,
                matterContacts: [{ contactId: createdContact._id!, relationshipType: 'client' }],
              })
              .pipe(
                map((createdMatter) =>
                  CrmActions.createIntakeSuccess({
                    contact: createdContact,
                    matter: createdMatter,
                  })
                )
              )
          ),
          catchError((error) =>
            of(CrmActions.createIntakeFailure({
              error: error?.error?.message || 'Failed to create intake',
            }))
          )
        )
      )
    )
  );

  loadMatterDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.loadMatterDocuments),
      switchMap(({ matterId }) =>
        this.apiService.getDocumentsByMatter(matterId).pipe(
          map((documents) =>
            CrmActions.loadMatterDocumentsSuccess({ documents })
          ),
          catchError((error) =>
            of(CrmActions.loadMatterDocumentsFailure({
              error: error?.error?.message || 'Failed to load documents',
            }))
          )
        )
      )
    )
  );

  uploadMatterDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.uploadMatterDocument),
      switchMap(({ matterId, formData }) =>
        this.apiService.uploadDocument(formData).pipe(
          tap(() => {
            // Fire-and-forget AI ingestion — errors don't block the CRM upload
            this.apiService.uploadDocumentForAI(formData).subscribe({
              error: (err) => console.warn('AI ingestion failed for uploaded document:', err),
            });
          }),
          map((document) => CrmActions.uploadMatterDocumentSuccess({ document })),
          catchError((error) =>
            of(CrmActions.uploadMatterDocumentFailure({
              error: error?.error?.message || 'Failed to upload document',
            }))
          )
        )
      )
    )
  );

  refreshDocumentsAfterUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.uploadMatterDocumentSuccess),
      map(({ document }) =>
        CrmActions.loadMatterDocuments({ matterId: document.matterId })
      )
    )
  );

  deleteMatterDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteMatterDocument),
      mergeMap(({ documentId, filename, matterId }) =>
        forkJoin([
          this.apiService.deleteDocument(documentId),
          this.apiService.purgeAiDocumentByFilename(filename),
        ]).pipe(
          map(() => CrmActions.deleteMatterDocumentSuccess({ documentId, matterId })),
          catchError((error) =>
            of(CrmActions.deleteMatterDocumentFailure({
              error: error?.error?.message || 'Failed to delete document',
            }))
          )
        )
      )
    )
  );

  refreshDocumentsAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteMatterDocumentSuccess),
      map(({ matterId }) => CrmActions.loadMatterDocuments({ matterId }))
    )
  );

  createMatterTimelineEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createMatterTimelineEvent),
      switchMap(({ matterId, event }) =>
        this.apiService.createMatterTimelineEvent(matterId, event).pipe(
          map(() => CrmActions.createMatterTimelineEventSuccess({ matterId })),
          catchError((error) =>
            of(CrmActions.createMatterTimelineEventFailure({
              error: error?.error?.message || 'Failed to create timeline event',
            }))
          )
        )
      )
    )
  );

  refreshTimelineAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.createMatterTimelineEventSuccess),
      map(({ matterId }) => CrmActions.loadMatterTimeline({ matterId, order: 'desc', limit: 20 }))
    )
  );

  updateMatterTimelineEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateMatterTimelineEvent),
      switchMap(({ matterId, eventId, event }) =>
        this.apiService.updateMatterTimelineEvent(matterId, eventId, event).pipe(
          map(() => CrmActions.updateMatterTimelineEventSuccess({ matterId })),
          catchError((error) =>
            of(CrmActions.updateMatterTimelineEventFailure({
              error: error?.error?.message || 'Failed to update timeline event',
            }))
          )
        )
      )
    )
  );

  refreshTimelineAfterTimelineUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.updateMatterTimelineEventSuccess),
      map(({ matterId }) => CrmActions.loadMatterTimeline({ matterId, order: 'desc', limit: 20 }))
    )
  );

  archiveMatterTimelineEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.archiveMatterTimelineEvent),
      switchMap(({ matterId, eventId }) =>
        this.apiService.archiveMatterTimelineEvent(matterId, eventId).pipe(
          map(() => CrmActions.archiveMatterTimelineEventSuccess({ matterId })),
          catchError((error) =>
            of(CrmActions.archiveMatterTimelineEventFailure({
              error: error?.error?.message || 'Failed to archive timeline event',
            }))
          )
        )
      )
    )
  );

  refreshTimelineAfterArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.archiveMatterTimelineEventSuccess),
      map(({ matterId }) => CrmActions.loadMatterTimeline({ matterId, order: 'desc', limit: 20 }))
    )
  );

  deleteMatterTimelineEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteMatterTimelineEvent),
      switchMap(({ matterId, eventId }) =>
        this.apiService.deleteMatterTimelineEvent(matterId, eventId).pipe(
          map(() => CrmActions.deleteMatterTimelineEventSuccess({ matterId })),
          catchError((error) =>
            of(CrmActions.deleteMatterTimelineEventFailure({
              error: error?.error?.message || 'Failed to delete timeline event',
            }))
          )
        )
      )
    )
  );

  refreshTimelineAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CrmActions.deleteMatterTimelineEventSuccess),
      map(({ matterId }) => CrmActions.loadMatterTimeline({ matterId, order: 'desc', limit: 20 }))
    )
  );

  constructor(private actions$: Actions, private apiService: ApiService) {}
}
